/**
 * Sticker artwork barrel.
 *
 * Earned stickers are flat-vector raster art (PNG, transparent) generated
 * with Nano Banana Pro and cut out + downscaled by scripts/process_badges.py
 * (see docs/sticker-design-brief.md + memory project_badge_art_direction).
 * Vite hashes/serves each import as a URL string.
 *
 * The map is keyed by the SAME ids the backend issues for achievements
 * (functions/lib/achievements.ts, streak.ts, mastery.ts) and the seed in
 * migrations/0002. Two ids are *frames* with a blank centre — the mastered
 * word / alphabet letter is drawn as a live text overlay in StickerEmblem,
 * so one frame serves every word and every letter:
 *   - `mastered_template` — hexagon frame for all mastered_<word> stickers
 *   - `alphabet_frame`    — tile frame for all 26 letters
 *
 * Unearned streak/total stickers don't use this map at all — they render a
 * bare silhouette shape in StickerEmblem so the milestone number can't leak
 * through (spec §8 surprise-reward principle).
 */

// Firsts
import firstPractice from "./badges/first_practice.png";
import firstWin from "./badges/first_win.png";
import firstPerfect from "./badges/first_perfect.png";

// Per-session
import perfect from "./badges/perfect.png";
import wroteItOut from "./badges/wrote_it_out.png";
import betterThanYesterday from "./badges/better_than_yesterday.png";
import comeback from "./badges/comeback.png";
import cleanSweep from "./badges/clean_sweep.png";

// Streaks
import streak3 from "./badges/streak_3.png";
import streak7 from "./badges/streak_7.png";
import streak14 from "./badges/streak_14.png";
import streak30 from "./badges/streak_30.png";
import streak100 from "./badges/streak_100.png";

// Totals
import sessions10 from "./badges/sessions_10.png";
import sessions25 from "./badges/sessions_25.png";
import sessions50 from "./badges/sessions_50.png";
import sessions100 from "./badges/sessions_100.png";
import mastered50 from "./badges/mastered_50.png";
import mastered100 from "./badges/mastered_100.png";

// School years
import year1Complete from "./badges/year_1_complete.png";
import year2Complete from "./badges/year_2_complete.png";

// Frames (blank centre + live text overlay)
import masteredTemplate from "./badges/mastered_template.png";
import alphabetFrame from "./badges/alphabet_frame.png";

/** Earned sticker art, keyed by backend achievement id. Values are
 *  Vite-resolved image URLs. */
export const STICKER_PNGS: Readonly<Record<string, string>> = Object.freeze({
  first_practice: firstPractice,
  first_win: firstWin,
  first_perfect: firstPerfect,

  perfect,
  wrote_it_out: wroteItOut,
  better_than_yesterday: betterThanYesterday,
  comeback,
  clean_sweep: cleanSweep,

  streak_3: streak3,
  streak_7: streak7,
  streak_14: streak14,
  streak_30: streak30,
  streak_100: streak100,

  // Totals — keys MUST match backend ids (sessions_* / mastered_*), not
  // any filename convention. A mismatch silently falls back to a blank
  // silhouette (the Totals bug we fixed in this redesign).
  sessions_10: sessions10,
  sessions_25: sessions25,
  sessions_50: sessions50,
  sessions_100: sessions100,
  mastered_50: mastered50,
  mastered_100: mastered100,

  year_1_complete: year1Complete,
  year_2_complete: year2Complete,
});

/** Frame art for the two text-overlay categories. */
export const STICKER_FRAMES = Object.freeze({
  mastery: masteredTemplate,
  alphabet: alphabetFrame,
});
